import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { fetchCcnFromCmsPos, type CcnFromCmsPosError } from "@services/facilityManagement";
import { getApiOrigin } from "@utils/apiOrigin";
import { isGlobalAdministrator } from "@utils/userUtils";
import { CmsLookupAdminDebugPanel, type CmsAdminDebugSnapshot } from "./CmsLookupAdminDebugPanel";
import { CmsLookupOutcomeFailure, CmsLookupOutcomeSuccess } from "./cmsLookupOutcomeIcons";

export interface CcnCmsLookupButtonProps {
  facilityId: number;
  disabled?: boolean;
  /** Called with CMS PRVDR_NUM when lookup succeeds (caller should update profile ``ccn``). */
  onResolved: (ccn: string) => void;
}

function ccnLookupRequestUrl(facilityId: number, forceRefresh: boolean): string {
  const base = getApiOrigin().replace(/\/+$/, "");
  const qs = forceRefresh ? "?force_refresh=true" : "";
  return `${base}/api/v1/facility-management/facility/${facilityId}/ccn-from-cms-pos${qs}`;
}

function userFacingCcnErrorMessage(err: CcnFromCmsPosError): string {
  if (err.status === 401 || err.status === 403) {
    return "You are not allowed to look up CCN for this facility, or your session expired. Sign in again or ask an administrator.";
  }
  if (err.status === 404) {
    return "The CCN lookup service was not found. Your app may be pointed at the wrong server version.";
  }
  if (err.status >= 500) {
    return "The server had a problem completing the CCN lookup. Try again in a few minutes.";
  }
  if (!err.status) {
    return err.message || "CCN lookup could not be completed. Check your connection and try again.";
  }
  return err.message || `CCN lookup failed (HTTP ${err.status}).`;
}

/**
 * Resolves CMS CCN (PRVDR_NUM) from Provider of Services using the facility’s name, state, and city.
 * Does not save the profile — user must save after verifying the value.
 * Global administrators also see request URL and response or error details below.
 */
const CcnCmsLookupButton: React.FC<CcnCmsLookupButtonProps> = ({
  facilityId,
  disabled = false,
  onResolved,
}) => {
  const isAdmin = isGlobalAdministrator();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CcnFromCmsPosError | null>(null);
  const [outcomeBadge, setOutcomeBadge] = useState<"success" | "error" | null>(null);
  const [successTooltip, setSuccessTooltip] = useState<string | null>(null);
  const [adminDebug, setAdminDebug] = useState<CmsAdminDebugSnapshot | null>(null);

  const runLookup = async () => {
    setLoading(true);
    setError(null);
    setOutcomeBadge(null);
    setSuccessTooltip(null);
    setAdminDebug(null);
    const requestUrl = ccnLookupRequestUrl(facilityId, true);
    try {
      const result = await fetchCcnFromCmsPos(facilityId, { forceRefresh: true });
      if (result.ok) {
        onResolved(result.data.ccn);
        const matched = result.data.matched_fac_name;
        setSuccessTooltip(
          matched
            ? `CCN filled from CMS. Matched facility name: ${matched}. Save your profile to keep it.`
            : "CCN filled from CMS. Save your profile to keep it.",
        );
        setOutcomeBadge("success");
        if (isAdmin) {
          setAdminDebug({
            outcome: "success",
            requestUrl,
            responseBody: result.data,
          });
        }
      } else {
        setError(result);
        setOutcomeBadge("error");
        if (isAdmin) {
          setAdminDebug({
            outcome: "error",
            requestUrl: result.url || requestUrl,
            status: result.status,
            message: result.message,
            error_code: result.error_code,
          });
        }
      }
    } catch (e) {
      const err: CcnFromCmsPosError = {
        ok: false,
        status: 0,
        message: e instanceof Error ? e.message : "CCN lookup failed",
        url: requestUrl,
      };
      setError(err);
      setOutcomeBadge("error");
      if (isAdmin) {
        setAdminDebug({
          outcome: "error",
          requestUrl,
          status: 0,
          message: err.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const errorTooltipTitle = error != null ? userFacingCcnErrorMessage(error) : "CCN lookup failed";

  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
          disabled={disabled || loading}
          onClick={runLookup}
          sx={{
            textTransform: "none",
            fontFamily: "Rethink Sans, sans-serif",
            borderColor: "#0e7490",
            color: "#0e7490",
          }}
        >
          {loading ? "Looking up CMS…" : "Look up CCN from CMS"}
        </Button>
        {outcomeBadge === "success" && !loading && successTooltip ? (
          <CmsLookupOutcomeSuccess title={successTooltip} ariaLabel="CCN lookup succeeded" />
        ) : null}
        {outcomeBadge === "error" && !loading && error ? (
          <CmsLookupOutcomeFailure title={errorTooltipTitle} ariaLabel="CCN lookup failed" />
        ) : null}
      </Box>
      {isAdmin ? (
        <CmsLookupAdminDebugPanel
          title="CCN from CMS POS — debug (global administrator)"
          snapshot={adminDebug}
        />
      ) : null}
    </Box>
  );
};

export default CcnCmsLookupButton;
